import type { ReactNode } from 'react';

/**
 * A small label chip used to communicate the status of a record.
 * The styling intentionally mirrors the tone of the dark-gold theme
 * tokens (`--gold-glow-*`, `--surface-hover`) so it blends into the
 * dashboard chrome without introducing new colors.
 *
 * The component is purely presentational. The list of `tone`s is
 * closed (we only ship the ones the EXCEL HR demo actually needs);
 * future pages should extend the union rather than pass raw strings.
 */
export type StatusTone =
  | 'success'
  | 'warning'
  | 'danger'
  | 'muted'
  | 'primary';

export type StatusBadgeProps = {
  /** Visible label inside the badge (e.g. "在职"). */
  label: string;
  /** Tone determines the background + text color. */
  tone?: StatusTone;
  /** Optional icon glyph; defaults to a small dot for visual rhythm. */
  withDot?: boolean;
};

const toneClass: Record<StatusTone, string> = {
  success: 'axi-status-badge is-success',
  warning: 'axi-status-badge is-warning',
  danger: 'axi-status-badge is-danger',
  muted: 'axi-status-badge is-muted',
  primary: 'axi-status-badge is-primary'
};

function StatusBadge({ label, tone = 'muted', withDot = true }: StatusBadgeProps) {
  return (
    <span className={toneClass[tone]}>
      {withDot ? <span className="axi-status-badge__dot" aria-hidden="true" /> : null}
      <span className="axi-status-badge__label">{label}</span>
    </span>
  );
}

/**
 * Convenience helper for status-tone choice. Keeps the call sites
 * short so each page reads naturally (e.g. `<StatusBadgeFor status={employee.status} />`).
 *
 * The mapping is **deliberately conservative**: only the values we
 * actually emit from `mock.ts` get a non-default tone. Anything
 * unknown falls back to `muted` so a future enum addition never
 * throws a missing-CSS-class regression.
 */
export function StatusBadgeFor({
  status,
  withDot = true
}: {
  status: string;
  withDot?: boolean;
}): ReactNode {
  const tone: StatusTone = ((): StatusTone => {
    switch (status) {
      case '在职':
      case '正常':
      case '已通过':
      case '已发offer':
        return 'success';
      case '试用期':
      case '面试中':
      case '迟到':
      case '招聘中':
        return 'primary';
      case '请假':
      case '加班':
      case '待审批':
        return 'warning';
      case '缺勤':
      case '已驳回':
      case '已关闭':
      case '离职':
        return 'danger';
      default:
        return 'muted';
    }
  })();
  return <StatusBadge label={status} tone={tone} withDot={withDot} />;
}

export default StatusBadge;